/**
 * PlayerProfileCard — reusable identity block shared by VS TEMPO / VS Robot /
 * VS Humano. Layout matches the mockup: avatar (top-left) + name (right of
 * avatar), school (optional, full width), nationality dropdown (full width
 * with flag). The card stays minimal — the parent screen controls the bigger
 * title and surrounding game-selection UI.
 *
 * Persistence is delegated to the parent (it receives the values + setters)
 * so that the legacy sessionStorage keys (`player_name`, etc.) keep working
 * with no migration. We just provide a consistent look-and-feel + handy
 * "Editar" affordance.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { NATIONALITIES, flagUrl, nationalityLabel } from "@/lib/nationalities";
import { AvatarSVG } from "@/lib/avatarBuilder";

export default function PlayerProfileCard({
  title,
  // Read-only "summary" mode (compact identity card with an Edit button) is
  // toggled by the parent via `editing`. When false we render the compact
  // view; when true we render the full form.
  editing,
  setEditing,
  name, setName,
  school, setSchool,
  nationality, setNationality,
  avatarCfg,
  onPickAvatar,
  testIdPrefix = "profile",
}) {
  const { t } = useI18n();

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm" data-testid={`${testIdPrefix}-card`}>
      {title && (
        <h2 className="text-3xl md:text-4xl font-[Fredoka] font-extrabold text-slate-900 mb-4 tracking-tight" data-testid={`${testIdPrefix}-title`}>
          {title}
        </h2>
      )}

      {!editing ? (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onPickAvatar}
              data-testid={`${testIdPrefix}-pick-avatar`}
              className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-sky-400 shrink-0"
              type="button"
              title={t("avatar_customize")}
            >
              <AvatarSVG cfg={avatarCfg} size={64} />
            </button>
            <div className="leading-tight">
              <div className="font-[Fredoka] font-bold text-slate-900 text-lg" data-testid={`${testIdPrefix}-summary-name`}>{name}</div>
              {school && <div className="text-sm text-slate-600">{school}</div>}
              <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                {flagUrl(nationality) && <img src={flagUrl(nationality)} alt={nationality} className="w-5 h-3.5 rounded-sm object-cover" />}
                {nationalityLabel(nationality)}
              </div>
            </div>
          </div>
          <button
            data-testid={`${testIdPrefix}-edit`}
            onClick={() => setEditing(true)}
            className="text-xs font-bold text-slate-500 hover:text-sky-600 underline"
            type="button"
          >
            {t("edit_btn") || "Editar"}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onPickAvatar}
              data-testid={`${testIdPrefix}-pick-avatar`}
              className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-sky-400 shrink-0"
              type="button"
              title={t("avatar_customize")}
            >
              <AvatarSVG cfg={avatarCfg} size={64} />
            </button>
            <div className="flex-1">
              <Label>{t("your_name")}</Label>
              <Input
                data-testid={`${testIdPrefix}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>
              {t("school")} <span className="text-slate-400 text-xs">({t("optional")})</span>
            </Label>
            <Input
              data-testid={`${testIdPrefix}-school`}
              value={school}
              onChange={(e) => setSchool(e.target.value)}
            />
          </div>
          <div>
            <Label>{t("your_nationality")}</Label>
            <Select value={nationality} onValueChange={setNationality}>
              <SelectTrigger data-testid={`${testIdPrefix}-nationality`}><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {NATIONALITIES.map((n) => (
                  <SelectItem key={n.code} value={n.code} data-testid={`nat-${n.code}`}>
                    <span className="inline-flex items-center gap-2">
                      {flagUrl(n.code) && <img src={flagUrl(n.code)} alt={n.code} className="w-5 h-3.5 rounded-sm object-cover" />}
                      {n.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
